/**
 * Shared types — domain models for CanvaLoop.
 * Per-page private types live in pages/<module>/types/.
 */

import type { RecordModel } from "pocketbase";

/* ────────────────────────────────────────────────────────────
   Re-export PB record base for convenience
   ──────────────────────────────────────────────────────────── */
export type { RecordModel };

/* ────────────────────────────────────────────────────────────
   App routes (display labels resolved in TopNav)
   ──────────────────────────────────────────────────────────── */
export const ROUTES = {
  DASHBOARD:   "/dashboard",
  /**
   * Legacy Studio route — kept in the constant for any old reference
   * but the actual route now 301-redirects to /dashboard. See App.tsx.
   * Dashboard's PromptDeck is the only generation surface in v2.
   */
  STUDIO:      "/studio",
  EDITOR:      "/edit",
  LIBRARY:     "/library",
  CALENDAR:    "/calendar",
  BRAND_KIT:   "/brand-kit",
  TEMPLATES:   "/templates",
  INSPIRATION: "/inspiration",
  WORKSPACES:  "/workspaces",
  SETTINGS:    "/settings",
  TEAM:        "/team",
  PLANS:       "/plans",
  ADMIN:       "/admin",
  LOGIN:       "/login",
  SIGNUP:      "/signup",
  ONBOARDING_WORKSPACE: "/onboarding/workspace",
  /* Public marketing + legal pages. No auth required. */
  PRICING:     "/pricing",
  TERMS:       "/terms",
  PRIVACY:     "/privacy",
  COOKIES:     "/cookies",
  REFUND:      "/refund",
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

/* ────────────────────────────────────────────────────────────
   Domain models (PB-shaped, extends RecordModel)
   ──────────────────────────────────────────────────────────── */

export interface UserProfile extends RecordModel {
  email: string;
  displayName?: string;
  name?: string;          // PB native
  avatar?: string;        // PB file
  bio?: string;
  role?: string;
  /** Grants access to the in-app Admin page. Set server-side only. */
  isSystemAdmin?: boolean;
  emailVisibility?: boolean;
  verified?: boolean;
}

export type MembershipRole = "editor" | "viewer";

export interface Membership extends RecordModel {
  workspaceId: string;
  userId: string;
  role: MembershipRole;
  addedBy?: string;
  /** Populated via PB `expand=userId` when listing. */
  expand?: {
    userId?: UserProfile;
    workspaceId?: Workspace;
  };
}

export interface Activity extends RecordModel {
  userId: string;
  workspaceId?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  meta?: Record<string, unknown>;
  expand?: {
    userId?: UserProfile;
    workspaceId?: Workspace;
  };
}

export interface UsageEvent extends RecordModel {
  userId: string;
  workspaceId?: string;
  model: string;
  kind: "image" | "text" | "edit";
  quality?: string;
  quantity?: number;
  cost: number;
  inputBytes?: number;
  outputBytes?: number;
  meta?: Record<string, unknown>;
  expand?: {
    userId?: UserProfile;
    workspaceId?: Workspace;
  };
}

export interface TypographyStyle {
  id: string;
  name: string;
  heading: string;
  body: string;
}

export interface Workspace extends RecordModel {
  name: string;
  category?: string;
  description?: string;
  voice?: string;         // short tone descriptor, e.g. "confident, warm, editorial"
  banned?: string[];      // visual tropes or words to avoid
  logo?: string;          // PB file
  colors?: string[];      // hex codes (source of truth for Gemini)
  typography?: TypographyStyle;
  userId: string;
}

export interface Preset extends RecordModel {
  name: string;
  description?: string;
  thumbnail?: string;     // PB file
  references?: string[];  // PB multi-file
  systemPrompt: string;
  team?: string[];        // emails
  userId: string;
}

export interface Folder extends RecordModel {
  name: string;
  workspaceId: string;
  userId: string;
}

/**
 * Rich generation metadata attached to every Asset created by the app.
 * Stored as JSON so we can extend without schema churn.
 */
export interface AssetMeta {
  /** Which surface produced the asset. */
  source?: "inspiration-engine" | "studio" | "upload" | "editor";
  /** Aspect ratio as passed to the image model, e.g. "3:4" or "21:9". */
  aspectRatio?: string;
  /** Rendered resolution — "1K" | "2K" | "4K". */
  quality?: string;
  /** Number of posts the user asked for in the batch this asset was part of. */
  count?: number;
  /** Topic the user typed in the Inspiration Engine (if any). */
  topic?: string;
  /** Full post idea the user wrote (if any). */
  postIdea?: string;
  /** Idea title produced by ideation ("Ishq in Every Frame"). */
  ideaTitle?: string;
  /** One-liner rationale from the ideation step. */
  ideaOneLiner?: string;
  /** Short tag / category label from ideation ("concert", "editorial", …). */
  ideaTag?: string;
  /** Internal: which model ID rendered the image. Do NOT display in user-facing UI. */
  modelName?: string;
  /** If this asset was produced by editing another, the source asset's id. */
  sourceAssetId?: string;
}

export interface Asset extends RecordModel {
  file?: string;          // PB file (preferred for new uploads)
  url?: string;           // legacy URL fallback
  thumbnail?: string;     // PB file
  thumbnailUrl?: string;
  type: "image" | "video";
  prompt: string;
  description?: string;
  size?: number;
  isFavorite?: boolean;
  uploaded?: boolean;
  presetName?: string;
  systemPrompt?: string;
  referenceUrls?: string[];
  /** Which ContentMode produced this asset — drives per-mode tooling in the Library. */
  mode?: "balanced" | "visual-first" | "editorial" | "type-driven" | "studio" | "grid-row";
  /** Rich generation metadata — see AssetMeta. */
  meta?: AssetMeta;
  /** Brand element IDs referenced in the generation. Expand on fetch to show thumbnails. */
  brandElementIds?: string[];
  /** PB's expand payload — populated when listAssets/getAsset is called with expand. */
  expand?: {
    brandElementIds?: BrandElement[];
  };
  folderId?: string;
  workspaceId: string;
  userId: string;
}

export interface BrandElement extends RecordModel {
  name: string;           // slug-ish short handle, unique per workspace
  description?: string;   // optional semantic context for the image model
  file: string;           // PB file (required)
  workspaceId: string;
  userId: string;
}

export interface CreativeIdea extends RecordModel {
  title: string;
  oneLiner?: string;
  prompt: string;
  thumbnail?: string;
  thumbnailUrl?: string;
  tag?: string;
  workspaceId: string;
  userId: string;
}

export type SocialPlatform = "instagram" | "tiktok" | "linkedin" | "twitter";

export interface ScheduledPost extends RecordModel {
  assetId: string;
  date: string;           // ISO
  platforms: SocialPlatform[];
  title?: string;
  caption?: string;
  status: "draft" | "scheduled" | "published";
  workspaceId: string;
  userId: string;
}

/* ────────────────────────────────────────────────────────────
   Phase 0 — subscriptions + credits (LemonSqueezy + PB ledger)
   ──────────────────────────────────────────────────────────── */

export type PlanTier = "free" | "pro" | "studio";
export type SubscriptionStatus =
  | "active"
  | "cancelled"
  | "past_due"
  | "refunded"
  | "expired"
  | "trialing";

export interface Subscription extends RecordModel {
  userId: string;
  plan: PlanTier;
  status: SubscriptionStatus;
  lemonSqueezyId?: string;
  lemonSqueezyOrderId?: string;
  lemonSqueezyCustomerId?: string;
  lemonSqueezyVariantId?: string;
  /** LS customer-portal deep link, captured on subscription_created. */
  customerPortalUrl?: string;
  /** LS update-payment-method deep link, captured on subscription_created. */
  updatePaymentMethodUrl?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
  trialEndsAt?: string;
  seats?: number;
  raw?: Record<string, unknown>;
}

export interface CreditsBalanceRecord extends RecordModel {
  userId: string;
  plan: PlanTier;
  periodStart: string;
  periodEnd: string;
  granted: number;
  bonusGranted?: number;
  used: number;
  resetCadence: "daily" | "monthly";
  resetAt?: string;
}

/**
 * Aggregate balance shape returned by POST /api/cl/credits/me.
 * Drives the TopNav credit pill + out-of-credits modal + Settings
 * subscription card + avatar-dropdown plan status row.
 */
export interface CreditsAggregate {
  plan: PlanTier;
  status: SubscriptionStatus;
  seats: number;
  cancelledAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /** LS sub id — empty string when on Free or pre-LS legacy. */
  lemonSqueezyId: string | null;
  /** LS customer-portal URL captured on subscription_created. */
  manageUrl: string | null;
  /** LS update-payment-method URL captured on subscription_created. */
  updatePaymentUrl: string | null;
  balance: {
    granted: number;
    bonusGranted: number;
    used: number;
    remaining: number;
    periodStart: string | null;
    periodEnd: string | null;
    resetAt: string | null;
    resetCadence: "daily" | "monthly";
    dailyCap: number;
  };
  monthly: {
    used: number;
    cap: number;
    remaining: number;
  };
}

export interface VideoProject extends RecordModel {
  name: string;
  status: "draft" | "rendering" | "completed";
  timelineData?: unknown;
  metadata?: {
    duration?: number;
    resolution?: { width: number; height: number };
    trackCount?: number;
    thumbnailUrl?: string;
    aspectRatio?: string;
  };
  workspaceId: string;
  userId: string;
}
